import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from '../../../../i18n';

export interface WordOrderPlayerProps {
  correctAnswer: string;
  onSubmit: (answer: string) => void;
  disabled?: boolean;
}

/** Default design-time cell size (px) before scale; scale shrinks the whole block to fit the container. */
const LETTER_MAX_PX = 52;
const GAP_PX = 6;

function letterMetricsForWordLen(n: number): { letterMaxPx: number; gapPx: number } {
  if (n >= 9) return { letterMaxPx: 40, gapPx: 4 };
  if (n >= 8) return { letterMaxPx: 44, gapPx: 5 };
  if (n >= 7) return { letterMaxPx: 48, gapPx: 5 };
  return { letterMaxPx: LETTER_MAX_PX, gapPx: GAP_PX };
}
/** Space between slot row and pool row (matches previous 1.5rem at 16px root). */
const ROW_GAP_PX = 24;

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * WordOrder player component.
 *
 * Supports two interaction modes:
 * 1. **Tap-to-select** (works on mobile and desktop) — tap a letter to select
 *    it (highlighted), then tap a slot to place it. Tap a filled slot to select
 *    it for swapping or returning to pool.
 * 2. **Drag-and-drop** (desktop) — HTML5 native drag.
 *
 * Layout uses a fixed design size then **scale()** so both letter rows always
 * fit the container width (no horizontal scroll on long words / small phones).
 */
export const WordOrderPlayer: React.FC<WordOrderPlayerProps> = ({
  correctAnswer,
  onSubmit,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const letters = useMemo(() => shuffle(correctAnswer.split('')), [correctAnswer]);
  const [slots, setSlots] = useState<(string | null)[]>(() => Array(correctAnswer.length).fill(null));
  const [pool, setPool] = useState<string[]>(() => [...letters]);
  const [submitted, setSubmitted] = useState(false);

  const [selected, setSelected] = useState<{ source: 'pool' | 'slot'; index: number } | null>(null);
  const [dragging, setDragging] = useState<{ source: 'pool' | 'slot'; index: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const n = correctAnswer.length;
  const { letterMaxPx, gapPx } = useMemo(() => letterMetricsForWordLen(n), [n]);
  const contentWidth = useMemo(
    () => (n <= 0 ? 0 : n * letterMaxPx + (n - 1) * gapPx),
    [n, letterMaxPx, gapPx]
  );

  const rowHeight = letterMaxPx + 8;
  const innerHeightUnscaled = rowHeight * 2 + ROW_GAP_PX;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const readContentWidth = () => {
      const cs = getComputedStyle(el);
      const pl = parseFloat(cs.paddingLeft) || 0;
      const pr = parseFloat(cs.paddingRight) || 0;
      const w = el.clientWidth - pl - pr;
      if (w > 0) setContainerWidth(w);
    };

    readContentWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', readContentWidth);
      return () => window.removeEventListener('resize', readContentWidth);
    }

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w != null && w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = useMemo(() => {
    if (contentWidth <= 0 || containerWidth <= 0) return 1;
    return Math.min(1, containerWidth / contentWidth);
  }, [contentWidth, containerWidth]);

  const scaledHeight = innerHeightUnscaled * scale;

  // ── Auto-submit when all slots filled ──
  const attemptSubmit = useCallback(() => {
    if (submitted || pool.length > 0) return;
    const word = slots.join('');
    if (word.length !== correctAnswer.length) return;
    setSubmitted(true);
    onSubmit(word);
  }, [submitted, pool.length, slots, correctAnswer.length, onSubmit]);

  useEffect(() => {
    if (pool.length === 0 && slots.every((s) => s !== null)) {
      attemptSubmit();
    }
  }, [pool.length, slots, attemptSubmit]);

  const isInactive = disabled || submitted;

  const handleTapPool = (index: number) => {
    if (isInactive) return;

    if (selected?.source === 'slot') {
      const poolLetter = pool[index];
      const slotLetter = slots[selected.index];
      if (slotLetter) {
        setSlots((prev) => {
          const next = [...prev];
          next[selected.index] = poolLetter;
          return next;
        });
        setPool((prev) => {
          const removed = prev.filter((_, i) => i !== index);
          removed.push(slotLetter);
          return removed;
        });
        setSelected(null);
        return;
      }
    }

    if (selected?.source === 'pool' && selected.index === index) {
      setSelected(null);
    } else {
      setSelected({ source: 'pool', index });
    }
  };

  const handleTapSlot = (slotIndex: number) => {
    if (isInactive) return;

    if (selected?.source === 'pool') {
      const letter = pool[selected.index];
      const existing = slots[slotIndex];
      setPool((prev) => {
        const removed = prev.filter((_, i) => i !== selected.index);
        if (existing) removed.push(existing);
        return removed;
      });
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = letter;
        return next;
      });
      setSelected(null);
      return;
    }

    if (selected?.source === 'slot') {
      if (selected.index === slotIndex) {
        setSelected(null);
        return;
      }
      setSlots((prev) => {
        const next = [...prev];
        const a = prev[selected.index];
        const b = prev[slotIndex];
        next[selected.index] = b;
        next[slotIndex] = a;
        return next;
      });
      setSelected(null);
      return;
    }

    if (slots[slotIndex]) {
      setSelected({ source: 'slot', index: slotIndex });
    }
  };

  const handleTapPoolArea = () => {
    if (isInactive) return;
    if (selected?.source === 'slot') {
      const letter = slots[selected.index];
      if (letter) {
        setSlots((prev) => {
          const next = [...prev];
          next[selected.index] = null;
          return next;
        });
        setPool((prev) => [...prev, letter]);
      }
      setSelected(null);
    }
  };

  const handleDragStart = (source: 'pool' | 'slot', index: number) => {
    if (isInactive) return;
    setDragging({ source, index });
    setSelected(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = (slotIndex: number) => {
    if (!dragging || isInactive) return;
    if (dragging.source === 'slot') {
      setSlots((prev) => {
        const next = [...prev];
        const a = prev[dragging.index];
        const b = next[slotIndex];
        next[dragging.index] = b;
        next[slotIndex] = a;
        return next;
      });
    } else {
      const letter = pool[dragging.index];
      const existing = slots[slotIndex];
      setPool((prev) => {
        const removed = prev.filter((_, i) => i !== dragging!.index);
        if (existing) removed.push(existing);
        return removed;
      });
      setSlots((prev) => {
        const next = [...prev];
        next[slotIndex] = letter;
        return next;
      });
    }
    setDragging(null);
  };

  const handleDropOnPool = () => {
    if (!dragging || isInactive) return;
    if (dragging.source === 'slot') {
      const letter = slots[dragging.index];
      setSlots((prev) => {
        const next = [...prev];
        next[dragging!.index] = null;
        return next;
      });
      setPool((prev) => [...prev, letter!]);
    }
    setDragging(null);
  };

  const handleDragEnd = () => {
    setDragging(null);
  };

  const fontSize = letterMaxPx * 0.52;
  const gap = gapPx;

  const isSelected = (source: 'pool' | 'slot', index: number) =>
    selected?.source === source && selected.index === index;

  const selectedBorder = '2px solid #34D399';
  const selectedShadow = '0 0 8px rgba(52, 211, 153, 0.5)';
  const emptySlotBorder = '2px dashed rgba(var(--color-secondary-rgb), 0.6)';
  const emptySlotBackground = 'linear-gradient(135deg, rgba(var(--color-secondary-rgb), 0.08) 0%, rgba(var(--color-primary-rgb), 0.06) 100%)';
  const filledSlotBackground = 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.18) 0%, rgba(var(--color-secondary-rgb), 0.12) 100%)';
  const poolLetterBackground = 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.14) 0%, rgba(var(--color-secondary-rgb), 0.18) 100%)';

  const letterBoxStyle = (opts: {
    border: string;
    background: string;
    showShadow: boolean;
    cursor: 'default' | 'pointer';
  }) => ({
    width: letterMaxPx,
    minWidth: letterMaxPx,
    height: letterMaxPx + 8,
    border: opts.border,
    borderRadius: 8,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    fontSize: `${fontSize}px`,
    fontWeight: 'bold' as const,
    background: opts.background,
    cursor: opts.cursor,
    boxShadow: opts.showShadow ? selectedShadow : 'none',
    transition: 'background 0.15s, border 0.15s, box-shadow 0.15s',
  });

  if (n === 0) {
    return (
      <div
        ref={containerRef}
      style={{
        width: '100%',
        maxWidth: 520,
        minWidth: 0,
        alignSelf: 'stretch',
        margin: '0 auto',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: 'min(520px, 100%)',
        minWidth: 0,
        alignSelf: 'stretch',
        margin: '0 auto',
        padding: '1rem max(0.5rem, env(safe-area-inset-right)) 1rem max(0.5rem, env(safe-area-inset-left))',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        overflow: 'hidden',
        overflowX: 'clip',
        boxSizing: 'border-box',
      }}
    >
      {/* Scaled block: slots + pool rows share one transform so width always fits container */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          minHeight: scaledHeight,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: contentWidth * scale,
            height: scaledHeight,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              width: contentWidth,
              transform: `translateX(-50%) scale(${scale})`,
              transformOrigin: 'top center',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: `${gap}px`,
                flexWrap: 'nowrap',
                marginBottom: ROW_GAP_PX,
                minHeight: rowHeight,
                width: contentWidth,
              }}
            >
              {slots.map((letter, i) => (
                <div
                  key={`slot-${i}`}
                  data-slot-index={i}
                  onClick={() => handleTapSlot(i)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropOnSlot(i)}
                  style={{
                    ...letterBoxStyle({
                      border: isSelected('slot', i) ? selectedBorder : emptySlotBorder,
                      background: letter
                        ? isSelected('slot', i)
                          ? 'rgba(52, 211, 153, 0.25)'
                          : filledSlotBackground
                        : emptySlotBackground,
                      showShadow: isSelected('slot', i),
                      cursor: isInactive ? 'default' : 'pointer',
                    }),
                    color: 'var(--text-primary)',
                  }}
                  draggable={!!letter && !isInactive}
                  onDragStart={() => letter && handleDragStart('slot', i)}
                  onDragEnd={handleDragEnd}
                >
                  {letter ?? ''}
                </div>
              ))}
            </div>

            <div
              onClick={handleTapPoolArea}
              onDragOver={handleDragOver}
              onDrop={handleDropOnPool}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: `${gap}px`,
                flexWrap: 'nowrap',
                minHeight: rowHeight,
                width: contentWidth,
              }}
            >
              {pool.map((letter, i) => (
                <div
                  key={`pool-${i}-${letter}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTapPool(i);
                  }}
                  draggable={!isInactive}
                  onDragStart={() => handleDragStart('pool', i)}
                  onDragEnd={handleDragEnd}
                  style={{
                    ...letterBoxStyle({
                      border: isSelected('pool', i) ? selectedBorder : '2px solid var(--color-primary)',
                      background: isSelected('pool', i)
                        ? 'rgba(52, 211, 153, 0.25)'
                        : poolLetterBackground,
                      showShadow: isSelected('pool', i),
                      cursor: isInactive ? 'default' : 'pointer',
                    }),
                    color: 'var(--text-primary)',
                    touchAction: 'none',
                  }}
                >
                  {letter}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!submitted && pool.length > 0 && (
        <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          {t('bonus.player.wordOrderHint')}
        </p>
      )}
    </div>
  );
};
